import { useIsApiReady } from "@/hooks/use-api-ready";
import {
  organizationSettingsControllerGetOrganizationSettings,
  organizationSettingsControllerSetOrganizationSetting,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { SettingPair } from "@nnr/settings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type SettingValue<T extends SettingPair["key"]> = Extract<
  SettingPair,
  { key: T }
>["value"];

function useOrganizationSettingsData(organizationUuid?: string | null) {
  const queryClient = useQueryClient();
  const isApiReady = useIsApiReady();

  const { data: settings, isPending } = useQuery({
    enabled: isApiReady && !!organizationUuid,
    queryKey: queryKeys.organizationSettings(organizationUuid ?? ""),
    queryFn: () =>
      organizationSettingsControllerGetOrganizationSettings({
        path: { organizationUuid: organizationUuid! },
      }).then((response) => response.data?.data ?? []),
  });

  const { mutateAsync: setValue } = useMutation({
    mutationFn: async (newSetting: SettingPair) => {
      if (!organizationUuid) {
        throw new Error("Organization UUID is required");
      }
      const response =
        await organizationSettingsControllerSetOrganizationSetting({
          path: { organizationUuid },
          body: newSetting,
        });
      if (!response.response.ok) {
        throw new Error("Failed to update organization setting");
      }
    },
    onSettled: () => {
      if (!organizationUuid) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizationSettings(organizationUuid),
      });
    },
  });

  return {
    settings: settings ?? [],
    setValue,
    isLoading: isPending && !!organizationUuid,
  };
}

export function useOrganizationSettingData<T extends SettingPair["key"]>(
  organizationUuid: string | null | undefined,
  key: T,
) {
  const { settings, isLoading, setValue } =
    useOrganizationSettingsData(organizationUuid);
  const value =
    (settings.find((setting) => setting.key === key)?.value as
      | SettingValue<T>
      | undefined) ?? null;

  return {
    value,
    setValue: async (newValue: SettingValue<T>) => {
      await setValue({ key, value: newValue } as SettingPair);
    },
    isLoading,
  };
}
